import { useState, useCallback } from 'react';
import { SortDirection } from './SortableTableHeader';
import { compareValues } from './compareValues';

interface UseSortableTableOptions<T extends string> {
    /** Initial sort field */
    initialSortBy?: T;
    /** Initial sort direction */
    initialDirection?: SortDirection;
    /** Comparator function for sorting objects */
    comparator?: (a: any, b: any, sortBy: T, direction: SortDirection) => number;
}

interface UseSortableTableResult<T extends string> {
    /** Current sort field */
    sortBy: T;
    /** Current sort direction */
    sortDirection: SortDirection;
    /** Handler function for sort operations */
    handleSort: (newSortBy: T) => void;
    /** Function to sort an array based on current sort state */
    sortItems: <U>(items: U[]) => U[];
}

/**
 * Custom hook for handling sortable table functionality
 * 
 * @param options Configuration options for the sortable table
 * @returns Object with sort state and handlers
 */
export function useSortableTable<T extends string>(options: UseSortableTableOptions<T> = {}): UseSortableTableResult<T> {
    const {
        initialSortBy,
        initialDirection = 'desc',
        comparator,
    } = options;

    const [sortDirection, setSortDirection] = useState<SortDirection>(initialDirection);
    const [sortBy, setSortBy] = useState<T>(initialSortBy as T);

    const handleSort = useCallback((newSortBy: T) => {
        if (sortBy === newSortBy) {
            setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(newSortBy);
            setSortDirection('desc');
        }
    }, [sortBy, sortDirection]);

    const sortItems = useCallback(<U>(items: U[]): U[] => {
        if (!sortBy || !items.length) return items;

        return [...items].sort((a, b) => {
            if (comparator) {
                return comparator(a, b, sortBy, sortDirection);
            }

            // Default sorting behavior if no comparator provided
            const aValue = (a as any)[sortBy];
            const bValue = (b as any)[sortBy];
            return compareValues(aValue, bValue, sortDirection);
        });
    }, [sortBy, sortDirection, comparator]);

    return {
        sortBy,
        sortDirection,
        handleSort,
        sortItems,
    };
}
